import type {
  IConfPersonas,
  IConfPersonasEmpleado,
  IConfPersonasEmpresa,
  IConfPersonasPaciente,
  IFormStructure
} from '@/modules/Administracion/interfaces'
import * as pdfMake from 'pdfmake/build/pdfmake'
import * as pdfFonts from 'pdfmake/build/vfs_fonts'
import type {
  Content,
  TDocumentDefinitions
} from 'pdfmake/interfaces'
import { styles } from '@/modules/Core/usings/pdf/pdfStyles'
import { getHeaderInfo, type IHeaderPdfData } from '@/modules/Core/usings/pdf/pacienteData'
import { getDocumentFooter } from '@/modules/Core/usings/pdf/pdfFooter'
import {
  agruparPorTipoOrden,
  getCertificadosData,
  getOrdersData,
  getOrdersTecnologiaData,
  type ICitaOrdenesAgrupadas
} from '@/modules/Core/usings/pdf/ordersData'
import { getDocumentHeader } from './pdf/pdfHeader'
import { getNotaPie, getNotaPieContent, NOTA_PIE_TIPOS_HABILITADOS } from './pdf/pdfNota'
import { headerCupsService, personaService } from '@/services'
import type { ICitaDiagnostico, ICitaTechnologiasSalud } from '@/modules/Asistencial/interfaces'
import { tipoImpresionAtencionDefault, tipoImpresionAsistencialDefault, tipoImpresionOrdenesDefault } from './constantes'
import { getComponentsData } from './pdf/componentData'
import type { IConfMaestra } from '@/modules/Configuracion/interfaces'
import type { IHeaderCups } from '@/modules/Administracion/interfaces/tablas/IHeaderCups'
import type { IConfCups } from '@/modules/Contratacion/interfaces/tablas/IConfCups'
import type { ICitaAdmision } from '@/modules/Admision/interfaces'

export interface IPdfRecordatorioCita {
  admision?: ICitaAdmision
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(<any>pdfMake).addVirtualFileSystem(pdfFonts)


/**
 *
 * @returns Imagen de cabecera para el PDF
 */
export const cupsDeTechnologias = (
  technologias?: (ICitaTechnologiasSalud | undefined)[] | null
): string => {
  const cups = [...new Set(
    (technologias || [])
      .map((t) => {
        const cup = t?.contratoxservicio?.cup
        return cup ? `${cup.cocuCups || ''} ${cup.cocuNombre || ''}`.trim() : ''
      })
      .filter(Boolean)
  )]
  return cups.length === 1 ? cups[0] : ''
}

export const getHeaderImage = async (technologia?: ICitaTechnologiasSalud | null) => {
  try {
    const { data: registro } = await headerCupsService.list()
    const cabeceras: IHeaderCups[] = (registro)?.body || []

    // 1) Intentar por CUPS de la tecnología
    const cupId = technologia?.contratoxservicio?.cup?.cocuId
    const cupCode = technologia?.contratoxservicio?.cup?.cocuCups

    const hasMatchingCup = (h: IHeaderCups): boolean => {
      if (!cupId && !cupCode) return false
      if (h.cocuId && cupId && h.cocuId === cupId) return true
      const list: IConfCups[] = Array.isArray(h.cups)
        ? (h.cups as IConfCups[])
        : (h.cups ? [h.cups as unknown as IConfCups] : [])
      return list.some((c) => (cupId && c.cocuId === cupId) || (cupCode && c.cocuCups === cupCode))
    }


    // Solo considerar cabeceras activas (cohcEstado === 1)
    let cabecera = cabeceras.find((h) => h.cohcEstado === 1 && Boolean(h.cohcHeader) && hasMatchingCup(h))

    // 2) Fallback: principal activa
    if (!cabecera) {
      cabecera = cabeceras.find((h) => h.cohcEstado === 1 && h.cohcIsPrincipal === 1 && Boolean(h.cohcHeader))
    }

    if (!cabecera || !cabecera.cohcHeader) return null
    const { data: { body: headerImage } } = await headerCupsService.getImagen({ cohcId: cabecera.cohcId as number })
    return headerImage
  } catch {
    return null
  }
}
export const getIps = async () => {
  const { data: { body: ips } } = await personaService.searchPersona<IConfPersonasEmpresa>('empresas', {isPrincipal:1})
  return ips.rows[0]
}
const getNombreHistoria = (tipo: tipoImpresionAsistencialDefault, nombre: string | undefined) => {
  let nombreHistoria = ''
  if (tipo === tipoImpresionAsistencialDefault.HISTORIA) {
    nombreHistoria = 'HISTORIA CLÍNICA DE ' + (nombre || '')
  } else {
    nombreHistoria = nombre || ''
  }
  // return tipo !== tipoImpresionAsistencialDefault.HISTORIA ? 'RESULTADO DE ' + nombreHistoria : nombreHistoria
  return nombreHistoria
}

export const getFirma = async (empleado:IConfPersonasEmpleado) => {
  try {
    const res = await firmaABase64(empleado?.copeId)
    // Validar respuesta: evitar usar "No data" u otros textos no base64
    const isValidBase64 = (data: unknown): data is string => {
      if (typeof data !== 'string') return false
      if (data === 'No data') return false
      const cleaned = data.replace(/^data:image\/\w+;base64,/, '')
      if (cleaned.length < 40) return false
      if (!/^[A-Za-z0-9+/=\r\n]+$/.test(cleaned)) return false
      return true
    }

    if (res && isValidBase64(res) && empleado) {
      empleado.copeFirma = res.startsWith('data:image') ? res : `data:image/png;base64,${res}`
    }
    if(!empleado?.copeFirma || !empleado.copeFirma.startsWith('data:image/png;base64,')) return [
      {
         margin: [0, 60, 0, 10],
          canvas: [
          { type: 'line', x1: 0, y1: 0, x2: 100, y2: 0, lineWidth: 1, lineColor: 'black' }
        ]
      },
      {
        text: `${empleado.copnNombre1} ${empleado.copnApellido1}`,
        style: 'tituloCell',
      },
      {
        text: empleado.coprPieFirma || '',
        style: 'textoCell',
      },
      {
        text: empleado.cargo?.comaNombrelargo || '',
        style: 'textoCell',
        pageBreak: 'after'
      }
    ]
    const firma = [
      {
        image: empleado.copeFirma,
        width: 120,
        height: 20,
        margin: [0, 10, 0, 10],
        border: [false, false, false, true],
      },
      {
        text: `${empleado.copnNombre1} ${empleado.copnApellido1}`,
        style: 'tituloCell',
      },
      {
        text: empleado.coprPieFirma || '',
        style: 'textoCell',
      },
      {
        text: empleado.cargo?.comaNombrelargo || '',
        style: 'textoCell',
        pageBreak: 'after'
      }
    ]
    return firma
  } catch (err) {
    console.warn('Firma no disponible (se continúa sin firma):', err)
    return [{
        text: '',
        pageBreak: 'after'
      }]
  }
}

export async function buildDocMedicoDefinition(
  contenido: IFormStructure,
  tipo: tipoImpresionAsistencialDefault = tipoImpresionAsistencialDefault.HISTORIA,
  historiWithOrdernes: boolean = false,
  isOdontologia: boolean = false,
  fechaStr?: string,
  tecnologiaNombre?: string
): Promise<TDocumentDefinitions | null> {
  if (!contenido || !contenido.admision) return null
  const ips = await getIps()
  let cups = ''

  let technologia:ICitaTechnologiasSalud | undefined
  if (Array.isArray(contenido.technologia)) {
    technologia = contenido.technologia[0]
  } else {
    technologia = contenido.technologia
  }
  const headerImage = await getHeaderImage(technologia)
  const titulos = {
    'HISTORIA': technologia?.contratoxservicio?.servicio?.coseNombre,
    'PROCEDIMIENTOS': technologia?.cittTechnologia,
    'OTROS SERVICIOS': technologia?.cittTechnologia,
    'LABORATORIOS': contenido.admision?.ciadTipoAdmision
  }
  const nombreHistoria = getNombreHistoria(tipo, titulos[tipo] || contenido.admision?.ciadTipoAdmision)
  cups = tipo === tipoImpresionAsistencialDefault.LABORATORIOS
    ? cupsDeTechnologias(contenido.admision?.technologias)
    : tipo == 'HISTORIA' ? cupsDeTechnologias([technologia]) : ''

  const nombrePaciente = contenido.admision?.paciente?.copeFullName || ''

  const content: unknown[] = []
  let firma
  if (technologia?.empleado) {
    try {
      const firmaEmpleado = await getFirma(technologia.empleado)
      if (firmaEmpleado && (firmaEmpleado as unknown[]).length > 0) {
        firma = firmaEmpleado
      }
    } catch (error) {
      console.warn('Error al obtener firma del empleado:', error)
    }
  }
  let notaPie: string | null = null
  if (NOTA_PIE_TIPOS_HABILITADOS.includes(tipo)) {
    const labsTramitados = tipo === tipoImpresionAsistencialDefault.LABORATORIOS
      ? (contenido.admision?.technologias || []).filter(
          (t) => t.cittTipoTechnologia === 'LABORATORIO' && t.cittEstado === 'TRAMITADO'
        )
      : []
    const tecnologiasNota = labsTramitados.length > 0
      ? labsTramitados
      : (technologia ? [technologia] : [])
    const cupsCodes = [...new Set(
      tecnologiasNota
        .map((t) => t.contratoxservicio?.cup?.cocuCups)
        .filter((c): c is string => !!c)
    )]
    const servicioId = tecnologiasNota
      .map((t) => t.contratoxservicio?.servicio?.coseId)
      .find((id) => !!id) ?? null
    notaPie = await getNotaPie({ cupsCodes, servicioId })
  }
  if (contenido && contenido.admision) {

    const dataHeaderPdf: IHeaderPdfData = {
      ...contenido.admision.paciente as IConfPersonasPaciente,
      technologia,
      fechaAtencion: contenido.admision.ciadFechaOrigen || technologia?.cittFechaInicio || technologia?.cittFechaEntrega || undefined,
      horaAtencion: technologia?.cittHora,
      numAutorizacion: technologia?.cittNumeroAutorizacion,
      contrato: technologia?.contratoxservicio?.contrato || contenido.admision.contrato
    }
    // Agregar tablas de paciente y tecnología SOLO si hay datos relevantes
    const tablasPaciente = await getHeaderInfo(dataHeaderPdf, contenido.admision)
    if (Object.keys(tipoImpresionAtencionDefault).includes(tipo)) {
      content.push(getDocumentHeader(ips, headerImage))
      const prefijos = {
        [tipoImpresionAtencionDefault.LABORATORIOS]: 'LAB-',
        [tipoImpresionAtencionDefault.PROCEDIMIENTOS]: 'PRO-',
        [tipoImpresionAtencionDefault['OTROS SERVICIOS']]: 'OTS-',
        [tipoImpresionAtencionDefault.HISTORIA]: 'HIST-',
        [tipoImpresionAtencionDefault.VACUNA]: 'VAC-',
      }
      const prefijo  = prefijos[tipo]
      const numId = tipo === tipoImpresionAtencionDefault.LABORATORIOS as string ? (contenido.admision.ciadId || technologia?.ciadId || '') : tipo === tipoImpresionAtencionDefault.PROCEDIMIENTOS as string ? (technologia?.cittId || '') : tipo === tipoImpresionAtencionDefault['OTROS SERVICIOS'] as string ? (technologia?.ciadId || '') : (technologia?.cittId || '')
      const header = [
        {
          text: 'REFERENCIA: ' + prefijo + numId,
          style: 'headerRight',
          border: [true, true, true, true],
          margin:[ 0, 5, 0, 0 ],
        },
        {
          text: nombreHistoria,
          style: 'header',
          border: [true, true, true, true],
          margin:[ 0, 5, 0, 0 ],
        }
      ]
      if (tipo !== tipoImpresionAsistencialDefault.LABORATORIOS || cups) {
        header.push({
            text: `${cups}`,
            style: 'subheader',
            border: [true, true, true, true],
            margin:[ 0, 0, 0, 5 ],
          })
      }
      content.push(header)
      content.push(getContent(technologia as ICitaTechnologiasSalud, contenido, tipo, firma, tablasPaciente, false, false, isOdontologia, ips, headerImage as string, fechaStr, tecnologiaNombre, notaPie))
      if (historiWithOrdernes && tipo === tipoImpresionAsistencialDefault.HISTORIA) {
        content.push(getOrdersPanel(technologia as ICitaTechnologiasSalud, contenido, tipo, firma, tablasPaciente, true, ips, headerImage as string))
      }
    } else {
      content.push(getOrdersPanel(technologia as ICitaTechnologiasSalud, contenido, tipo, firma, tablasPaciente, true, ips, headerImage as string))
    }

    removeLastPageBreak(content)
    const docDefinition: TDocumentDefinitions = {
        styles,
        pageMargins: [30, 20, 30, 50],
        content: content as Content,
        footer: getDocumentFooter(),
        info:{
            title: `${nombreHistoria}-${nombrePaciente}`,
            keywords: 'historia clínica, salud, IPS',
            creator: 'Analuu Tools',
        }
    }

    return docDefinition
  }

  return null
}

export async function generateDocMedico(
  contenido: IFormStructure,
  tipo: tipoImpresionAsistencialDefault = tipoImpresionAsistencialDefault.HISTORIA,
  historiWithOrdernes: boolean = false,
  returnAsBlob: boolean = false,
  isOdontologia: boolean = false
): Promise<Blob | void> {
  const docDefinition = await buildDocMedicoDefinition(
    contenido,
    tipo,
    historiWithOrdernes,
    isOdontologia
  )
  if (docDefinition) {
    if (returnAsBlob) {
      // Retornar como blob
      return new Promise((resolve) => {
        pdfMake.createPdf(docDefinition).getBlob((blob: Blob) => {
          resolve(blob)
        })
      })
    }
    // Abrir directamente
    pdfMake.createPdf(docDefinition).open()
    return
  }

  // Si no hay contenido válido y se requiere blob, retornar blob vacío
  if (returnAsBlob) {
    return new Blob([], { type: 'application/pdf' })
  }
}
export async function generateDocRecordatorio(
  contenido: IPdfRecordatorioCita,
  returnAsBlob: boolean = false
): Promise<Blob | void> {
  const ips = await getIps()

  const headerImage = await getHeaderImage()

  const content: unknown[] = []
  const admision = contenido.admision
  if (admision && admision.technologias && admision.technologias.length > 0) {
      content.push(getDocumentHeader(ips, headerImage, true))
      const tecnologia = admision.technologias[0]
        let servicio = ''
        const tipoAdmision = admision.ciadTipoAdmision
        switch (tipoAdmision) {
          case 'CONSULTA':
            servicio = tecnologia.contratoxservicio?.servicio?.coseNombre || 'Consulta médica'
            break
          case 'PROCEDIMIENTO':
            servicio = tecnologia.contratoxservicio?.cup?.cocuNombre || 'Procedimiento médico'
            break
          case 'LABORATORIO':
            servicio = 'LABORATORIO'
            break
          default:
            servicio = tipoAdmision || 'Servicio médico'
        }
        const fechaFormateada = admision.ciadFechaOrigen ? `${new Date(admision.ciadFechaOrigen).toLocaleString('es-CO')}` : 'No diligenciado'

      const data = [
        {
          text: `Agendamiento de ${tipoAdmision?.toLowerCase()}`,
          fontSize: 9,
          bold: true,
          alignment: 'center',
        },
        {
          text: `Pac.: ${admision.paciente?.copnNombre1 || ''} ${admision.paciente?.copnApellido1 || ''}\nIdent.:${admision.paciente?.tipoidentificacion?.comaNombrecorto || ''}-${admision.paciente?.copeIdentificacion || ''}`.trim(),
          style: 'itemTicked',
          margin: [0 , 10, 0, 0]
        },
        {
          text: `Serv.: ${servicio}`,
          style: 'itemTicked'
        },
        {
          text: `Con: ${tecnologia.empleado ? `${tecnologia.empleado.copnNombre1 || ''} ${tecnologia.empleado.copnApellido1 || ''}`.trim() : 'Médico por asignar'}`,
          style: 'itemTicked'
        },
        {
          text: `El: ${fechaFormateada}`,
          style: 'itemTicked'
        },
        { 
          text: `En:\n${ips.coemNombre}\n${ips.copeDireccion}\n${ips.copeTelefono}`,
          style: 'itemTicked',
          margin: [0 , 0, 0, 10]
        },
        {
          stack: [
            {
              text: 'Analuu Tools (HIS) - Sistema de información hospitalaria desarrollado por JA Software SAS',
              style: 'footerPageCenter'
            },
            {
              text: [
                { text: 'Para contactos acerca del software visita ' },
                { text: 'www.jasoftwaresas.com', link: 'https://www.jasoftwaresas.com', color: 'blue' }
              ],
              style: 'footerPageCenter'
            }
          ]
        }
      ]
    content.push(data)
  
    removeLastPageBreak(content)
    const docDefinition: TDocumentDefinitions = {
        styles,
        pageSize: {
            width: 164,
            height: 500
        },
        pageMargins: [5, 5, 5, 5],
        content: content as Content,
        info:{
            title: `Recordatorio cita`,
            keywords: 'recordatorio, salud, IPS',
            creator: 'Analuu Tools',
        }
    }

    if (returnAsBlob) {
      // Retornar como blob
      return new Promise((resolve) => {
        pdfMake.createPdf(docDefinition).getBlob((blob: Blob) => {
          resolve(blob)
        })
      })
    } else {
      // Abrir directamente
      pdfMake.createPdf(docDefinition).open()
    }
  }

  // Si no hay contenido válido y se requiere blob, retornar blob vacío
  if (returnAsBlob) {
    return new Blob([], { type: 'application/pdf' })
  }
}
const firmaABase64 = async (copeId:IConfPersonas['copeId']) => {
  if(!copeId) return null
  try {
    const response = await personaService.getFirma({ copeId, tipo: 'empleados' })
    const body = response?.data?.body
    if (!body || body === 'No data') return null
    return body
  } catch {
    return null
  }
}

const getContent = (technologia: ICitaTechnologiasSalud, contenido: IFormStructure,  tipo: string, firma: unknown[], datosPaciente: unknown[], orderWithCabezera: boolean = false, historiWithOrdernes: boolean = false, isOdontologia: boolean = false, ips: IConfPersonasEmpresa, headerImage: string, fechaStr?: string, tecnologiaNombre?: string, notaPie?: string | null) => {
  const content = [] as unknown[]
  if( !historiWithOrdernes && ( tipo === tipoImpresionAsistencialDefault.HISTORIA || tipo === tipoImpresionAsistencialDefault.PROCEDIMIENTOS || tipo === tipoImpresionAsistencialDefault.LABORATORIOS || tipo === tipoImpresionAsistencialDefault.VACUNA || tipo === tipoImpresionAsistencialDefault['OTROS SERVICIOS']) ) {
    content.push(getComponentsData(contenido, technologia, tipo, datosPaciente, fechaStr, tecnologiaNombre))
  }
  if (tipo === tipoImpresionAsistencialDefault.HISTORIA) {
    content.push(getOrdersPanel(technologia, contenido, tipo, firma, datosPaciente, orderWithCabezera, ips, headerImage))
  }
  if( !historiWithOrdernes ) {
    if(notaPie) content.push(getNotaPieContent(notaPie))
    if(firma) content.push(JSON.parse(JSON.stringify(firma)))
  }
  if(isOdontologia){
    for (const element of contenido.odontogramas || []) {
        if (element.svg) {
          content.push({
            text: element.titulo,
            alignment: 'center',
            style: 'header',
            margin: [0, 10, 0, 5],
            border: [false, true, false, true],
          })
          content.push({
            svg: element.svg.replace('width="1500" height="700"','width="1500" height="700" viewBox="0 0 1500 700" '),
            fit: [600, 400]
          })
        }
    }
    content.push({text:'',pageBreak: 'after'})
  }
  if (contenido.graficas && Object.keys(contenido.graficas).length > 0) {
    content.push({text: 'GRAFICAS',
      alignment: 'center',
      style: 'header',
      margin: [0, 10, 0, 5],
      border: [false, true, false, true]
    })
    for (const element of contenido.graficas || []) {
      if (element.base64) {
        content.push({
          image: element.base64,
          alignment: 'center',
          fit: [400, 400]
        })
      }
    }
    content.push({text:'',pageBreak: 'after'})
  }

  return content
}

export const getOrdersPanel = (technologia: ICitaTechnologiasSalud, contenido: IFormStructure,  tipo: string, firma: unknown[], datosPaciente: unknown[], orderWithCabezera: boolean = false, ips: IConfPersonasEmpresa, headerImage: string) => {
  const content = [] as unknown[]
  const maestras = contenido.maestras as IConfMaestra[] || []
  const diagnosticos = technologia.diagnosticos || []

  if(tipoImpresionAsistencialDefault.HISTORIA === tipo){
    for (const tipoOrden of Object.keys(tipoImpresionOrdenesDefault)) {
      if(getDataOrders[tipoOrden]){
        getDataOrders[tipoOrden](technologia, tipo, content, maestras, diagnosticos, firma, datosPaciente, orderWithCabezera, ips, headerImage)
      }
    }
  } else {
    getDataOrders[tipo](technologia, tipo, content, maestras, diagnosticos, firma, datosPaciente, orderWithCabezera, ips, headerImage)
  }
  return content
}

const getDataOrders = {
  'OTROS SERVICIOS': (technologia: ICitaTechnologiasSalud, tipo: string, content: Array<unknown>, maestras: IConfMaestra[], diagnosticos: ICitaDiagnostico[], firma: unknown[], datosPaciente: unknown[], orderWithCabezera: boolean = false, ips: IConfPersonasEmpresa, headerImage: string) =>{
    const techOrdenes = Array.isArray(technologia.ordenestecnologia) ? technologia.ordenestecnologia.filter(e => e.ciotTipo == tipoImpresionOrdenesDefault['OTROS SERVICIOS'] as string) : []
    if (techOrdenes && techOrdenes.length > 0) {
      content.push(getOrdersTecnologiaData(
        techOrdenes,
        { maestras,
          diagnosticos,
          visible: true,
          firma,
          datosPaciente,
          orderWithCabezera,
          ips,
          headerImage
        }
      ))
    }
  },
  ORDEN_LABORATORIO: (technologia: ICitaTechnologiasSalud, tipo: string, content: Array<unknown>, maestras: IConfMaestra[], diagnosticos: ICitaDiagnostico[], firma: unknown[], datosPaciente: unknown[], orderWithCabezera: boolean = false, ips: IConfPersonasEmpresa, headerImage: string) =>{
    const techOrdenes = Array.isArray(technologia.ordenestecnologia) ? technologia.ordenestecnologia.filter(e => e.ciotTipo == tipoImpresionOrdenesDefault.ORDEN_LABORATORIO as string) : []
    if (techOrdenes && techOrdenes.length > 0) {
      content.push(getOrdersTecnologiaData(
        techOrdenes,
        { maestras,
          diagnosticos,
          visible: true,
          firma,
          datosPaciente,
          orderWithCabezera,
              ips,
              headerImage
        }
      ))
    }
  },
  ORDEN_PROCEDIMIENTO: (technologia: ICitaTechnologiasSalud, tipo: string, content: Array<unknown>, maestras: IConfMaestra[], diagnosticos: ICitaDiagnostico[], firma: unknown[], datosPaciente: unknown[], orderWithCabezera: boolean = false, ips: IConfPersonasEmpresa, headerImage: string) =>{
    const techOrdenes = Array.isArray(technologia.ordenestecnologia) ? technologia.ordenestecnologia.filter(e => e.ciotTipo == tipoImpresionOrdenesDefault.ORDEN_PROCEDIMIENTO as string) : []
    if (techOrdenes && techOrdenes.length > 0) {
          content.push(getOrdersTecnologiaData(
            techOrdenes,
            { maestras,
              diagnosticos,
              visible: true,
              firma,
              datosPaciente,
              orderWithCabezera,
              ips,
              headerImage
            }
          ))
        }
  },
  ORDEN_REMISION: (technologia: ICitaTechnologiasSalud, tipo: string, content: Array<unknown>, maestras: IConfMaestra[], diagnosticos: ICitaDiagnostico[], firma: unknown[], datosPaciente: unknown[], orderWithCabezera: boolean = false, ips: IConfPersonasEmpresa, headerImage: string) =>{
    const techOrdenes = Array.isArray(technologia.ordenestecnologia) ? technologia.ordenestecnologia.filter(e => e.ciotTipo == tipoImpresionOrdenesDefault.ORDEN_REMISION as string) : []
    if (techOrdenes && techOrdenes.length > 0) {
          content.push(getOrdersTecnologiaData(
            techOrdenes,
            { maestras,
              diagnosticos,
              visible: true,
              firma,
              datosPaciente,
              orderWithCabezera,
              ips,
              headerImage
            }
          ))
        }
  },
  ORDEN_RECETARIO: (technologia: ICitaTechnologiasSalud, tipo: string, content: Array<unknown>, maestras: IConfMaestra[], diagnosticos: ICitaDiagnostico[], firma: unknown[], datosPaciente: unknown[], orderWithCabezera: boolean = false, ips: IConfPersonasEmpresa, headerImage: string) =>{
    const techOrdenes = Array.isArray(technologia.ordenestecnologia) ? technologia.ordenestecnologia.filter(e => e.ciotTipo == tipoImpresionOrdenesDefault.ORDEN_RECETARIO as string) : []
    if (techOrdenes && techOrdenes.length > 0) {
          content.push(getOrdersTecnologiaData(
            techOrdenes,
            { maestras,
              diagnosticos,
              visible: true,
              firma,
              datosPaciente,
              orderWithCabezera,
              ips,
              headerImage
            }
          ))
        }
  },
  [tipoImpresionOrdenesDefault.PROCEDIMIENTO_ATENCION]: (technologia: ICitaTechnologiasSalud, tipo: string, content: Array<unknown>, maestras: IConfMaestra[], diagnosticos: ICitaDiagnostico[], firma: unknown[], datosPaciente: unknown[], orderWithCabezera: boolean = false) =>{
    const ordenes = Array.isArray(technologia.ordenes) ? technologia.ordenes : []
    if (ordenes && ordenes.length > 0 && tipo === tipoImpresionAsistencialDefault.HISTORIA as string) {
          const agrupadas: ICitaOrdenesAgrupadas[] | null = agruparPorTipoOrden(ordenes)
          agrupadas?.forEach((ordenesAgrupadas: ICitaOrdenesAgrupadas) => {
            content.push(getOrdersData(ordenesAgrupadas,{
              maestras,
              diagnosticos,
              visible: !orderWithCabezera &&  tipo === tipoImpresionAsistencialDefault.HISTORIA as string,
              firma,
              datosPaciente,
              orderWithCabezera }))
          })
      }
  },
  [tipoImpresionOrdenesDefault.CERTIFICADOS]: (technologia: ICitaTechnologiasSalud, tipo: string, content: Array<unknown>, maestras: IConfMaestra[], diagnosticos: ICitaDiagnostico[], firma: unknown[], datosPaciente: unknown[], orderWithCabezera: boolean = false, ips: IConfPersonasEmpresa, headerImage: string) =>{
    const certificados = technologia.certificados || []
    if (certificados && certificados.length > 0) {
      content.push(getCertificadosData(
        certificados,
        tipo,
        {
          technologia,
          maestras,
          diagnosticos,
          visible: (tipo === tipoImpresionAsistencialDefault.CERTIFICADOS || tipo === tipoImpresionAsistencialDefault.HISTORIA),
          firma,
          datosPaciente,
          orderWithCabezera: (tipo === tipoImpresionAsistencialDefault.CERTIFICADOS ? tipo === tipoImpresionAsistencialDefault.CERTIFICADOS: orderWithCabezera),
              ips,
              headerImage
        }
      ))
    }
  },
  [tipoImpresionOrdenesDefault.INCAPACIDADES]: (technologia: ICitaTechnologiasSalud, tipo: string, content: Array<unknown>, maestras: IConfMaestra[], diagnosticos: ICitaDiagnostico[], firma: unknown[], datosPaciente: unknown[], orderWithCabezera: boolean = false, ips: IConfPersonasEmpresa, headerImage: string) =>{
    const incapacidades = technologia.incapacidades || []
     if (incapacidades && incapacidades.length > 0) {
       content.push(getCertificadosData(
        incapacidades,
        tipo,
        {
          technologia,
          maestras,
          diagnosticos,
          visible: tipo === tipoImpresionAsistencialDefault.INCAPACIDADES || tipo === tipoImpresionAsistencialDefault.HISTORIA,
          firma,
          datosPaciente,
          orderWithCabezera: (tipo === tipoImpresionAsistencialDefault.INCAPACIDADES ? tipo === tipoImpresionAsistencialDefault.INCAPACIDADES: orderWithCabezera),
              ips,
              headerImage
        }
      ))
    }
  }
 }

// Función para eliminar el último pageBreak del contenido para evitar hojas en blanco
export const removeLastPageBreak = (contentArray: unknown[]): void => {
  const findAndRemovePageBreak = (item: unknown): boolean => {
    if (Array.isArray(item)) {
      // Recorrer desde el final hacia el inicio del array
      for (let i = item.length - 1; i >= 0; i--) {
        if (findAndRemovePageBreak(item[i])) {
          return true // Si encontramos y eliminamos un pageBreak, salimos
        }
      }
    } else if (item && typeof item === 'object') {
      const obj = item as Record<string, unknown>
      if (obj.pageBreak === 'after') {
        delete obj.pageBreak
        return true
      }
      // Buscar en las propiedades del objeto
      const keys = Object.keys(obj).reverse()
      for (const key of keys) {
        if (findAndRemovePageBreak(obj[key])) {
          return true
        }
      }
    }
    return false
  }

  // Buscar desde el final del array principal
  for (let i = contentArray.length - 1; i >= 0; i--) {
    if (findAndRemovePageBreak(contentArray[i])) {
      break // Una vez que encontramos y eliminamos el último pageBreak, salimos
    }
  }
}
